import { FC } from 'react';
import { FormattedMessage, type IntlShape } from 'react-intl';
import { Badge, Button } from 'reactstrap';
import { getUnattributedPerson } from 'utils/models/Person';
import { stripHtml } from 'utils/util/util';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import Avatar from 'views/Widgets/People/Avatar';
import RelativeTime from 'views/Widgets/RelativeTime';
import ThreadedCommentComposer from './ThreadedCommentComposer';
import { countReplies, type ThreadedCommentNode } from './threadedComments';

interface Props {
  comment: ThreadedCommentNode;
  depth: number;
  rootId: number;
  isThreadExpanded: boolean;
  onToggleThread: (rootId: number) => void;
  canReply: boolean;
  activeReplyTarget?: number;
  onOpenReply: (commentId: number) => void;
  onCloseReply: () => void;
  replyText: string;
  onReplyTextChange: (value: string) => void;
  replyIsPrivate: boolean;
  onReplyIsPrivateChange: (value: boolean) => void;
  replyError: string | null;
  isSubmittingReply: boolean;
  onSubmitReply: (parentId: number) => void;
  formatMessage: IntlShape['formatMessage'];
  currentUserId?: number;
  onToggleIsPrivate?: (comment: ThreadedCommentNode) => void;
  // show the whole comment body instead of collapsing it behind "View more"
  showFullBody?: boolean;
}

const ThreadedCommentItem: FC<Props> = ({
  comment,
  depth,
  rootId,
  isThreadExpanded,
  onToggleThread,
  canReply,
  activeReplyTarget,
  onOpenReply,
  onCloseReply,
  replyText,
  onReplyTextChange,
  replyIsPrivate,
  onReplyIsPrivateChange,
  replyError,
  isSubmittingReply,
  onSubmitReply,
  formatMessage,
  currentUserId,
  onToggleIsPrivate,
  showFullBody = false,
}) => {
  const isReplying = activeReplyTarget === comment.id;
  const hasAuthor = !!comment.author;
  const isAuthor = !!currentUserId && comment.author?.id === currentUserId;
  const isRootComment = depth === 0;
  const repliesAvailable = countReplies(comment);
  const commentDate = comment.created_at ? new Date(comment.created_at) : null;
  const hasValidDate = commentDate && !Number.isNaN(commentDate.getTime());
  const hasCommentContent =
    stripHtml(comment.body ?? '', true).trim().length > 0;

  const showReplyButton = canReply;
  const showPrivacyToggle = isAuthor && !!onToggleIsPrivate;
  const showThreadToggle = isRootComment && repliesAvailable > 0;
  const showActionsRow =
    showReplyButton || showPrivacyToggle || showThreadToggle;

  const authorName =
    comment.author?.full_name ||
    formatMessage({
      id: 'app.views.widgets.comments.threaded_comment_item.author.unknown',
      defaultMessage: 'Someone',
    });

  return (
    <div>
      <div className="d-flex gap-3">
        <Avatar
          person={
            hasAuthor
              ? comment.author!
              : getUnattributedPerson(formatMessage, 0, null, true)
          }
          size="sm"
          className="flex-shrink-0"
        />
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="fw-semibold">{authorName}</span>
            {hasValidDate && (
              <span
                className="text-muted small"
                title={commentDate!.toISOString()}
              >
                <RelativeTime datetime={comment.created_at} />
              </span>
            )}
            {comment.is_private && (
              <Badge color="secondary" className="text-uppercase">
                <FormattedMessage
                  id="app.views.widgets.comments.threaded_comment_item.private_badge"
                  defaultMessage="Private"
                />
              </Badge>
            )}
          </div>
          <div className="threaded-comment-message mt-2 text-body">
            {hasCommentContent ? (
              <RichTextViewer model={comment.body} expanded={showFullBody} />
            ) : (
              <span className="fst-italic text-muted">
                <FormattedMessage
                  id="app.views.widgets.comments.threaded_comment_item.no_text"
                  defaultMessage="No additional details provided."
                />
              </span>
            )}
          </div>
          {showActionsRow && (
            <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
              {showReplyButton && (
                <Button
                  color="link"
                  size="sm"
                  className="px-0"
                  onClick={() => onOpenReply(comment.id)}
                >
                  <FormattedMessage
                    id="app.views.widgets.comments.threaded_comment_item.reply"
                    defaultMessage="Reply"
                  />
                </Button>
              )}
              {showPrivacyToggle && (
                <Button
                  color="link"
                  size="sm"
                  className="px-0"
                  onClick={() => onToggleIsPrivate!(comment)}
                >
                  {comment.is_private ? (
                    <FormattedMessage
                      id="app.views.widgets.comments.threaded_comment_item.make_public"
                      defaultMessage="Make public"
                    />
                  ) : (
                    <FormattedMessage
                      id="app.views.widgets.comments.threaded_comment_item.make_private"
                      defaultMessage="Make private"
                    />
                  )}
                </Button>
              )}
              {showThreadToggle && (
                <Button
                  color="link"
                  size="sm"
                  className="px-0"
                  onClick={() => onToggleThread(rootId)}
                >
                  {isThreadExpanded ? (
                    <FormattedMessage
                      id="app.views.widgets.comments.threaded_comment_item.hide_replies"
                      defaultMessage="Hide replies"
                    />
                  ) : (
                    <FormattedMessage
                      id="app.views.widgets.comments.threaded_comment_item.view_replies"
                      defaultMessage="View {count, number} {count, plural, one {reply} other {replies}}"
                      values={{ count: repliesAvailable }}
                    />
                  )}
                </Button>
              )}
            </div>
          )}
          {canReply && isReplying && (
            <ThreadedCommentComposer
              editorName={`reply-editor-${comment.id}`}
              content={replyText}
              onContentChange={onReplyTextChange}
              isPrivate={replyIsPrivate}
              onIsPrivateChange={onReplyIsPrivateChange}
              error={replyError}
              isSubmitting={isSubmittingReply}
              onSubmit={() => onSubmitReply(comment.id)}
              onCancel={onCloseReply}
              submitLabel={
                <FormattedMessage
                  id="app.views.widgets.comments.threaded_comment_item.submit_reply"
                  defaultMessage="Post reply"
                />
              }
              placeholder={formatMessage({
                id: 'app.views.widgets.comments.threaded_comment_item.reply_placeholder',
                defaultMessage: 'Reply...',
              })}
              autoFocus
            />
          )}
        </div>
      </div>
      {isThreadExpanded && comment.replies.length > 0 && (
        <div
          className="mt-3 ms-3 ps-3 d-flex flex-column gap-3"
          style={{ borderLeft: '6px solid var(--bs-border-color, #dee2e6)' }}
        >
          {comment.replies.map((reply) => (
            <ThreadedCommentItem
              key={reply.id}
              comment={reply}
              depth={1}
              rootId={rootId}
              isThreadExpanded={isThreadExpanded}
              onToggleThread={onToggleThread}
              canReply={canReply}
              activeReplyTarget={activeReplyTarget}
              onOpenReply={onOpenReply}
              onCloseReply={onCloseReply}
              replyText={replyText}
              onReplyTextChange={onReplyTextChange}
              replyIsPrivate={replyIsPrivate}
              onReplyIsPrivateChange={onReplyIsPrivateChange}
              replyError={replyError}
              isSubmittingReply={isSubmittingReply}
              onSubmitReply={onSubmitReply}
              formatMessage={formatMessage}
              currentUserId={currentUserId}
              onToggleIsPrivate={onToggleIsPrivate}
              showFullBody={showFullBody}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ThreadedCommentItem;
